import { useState, useEffect, useRef } from 'react';
import { Shot, ShotType, ShotResult, Weight, WeightReason, CurlAssessment, CurlReason, Turn, SpecialShot, ShotIssue, Game, PracticeShot, POSITION_ZONES, getZoneFromShotType } from '../types/curling';
import { Target, X, ArrowLeft, Clock, SkipForward, Sparkles, Star, Bomb, RotateCw, RotateCcw, Grid3x3, Hand, Play, Pause } from 'lucide-react';
import { ShotVisualizer, ShotVisualizerAppliedValues } from './ShotVisualizer';
import { ShotResultRecorder, ShotResultRecorderAppliedValues } from './ShotResultRecorder';
import { getPracticeSession } from '../utils/storage';
import { pushCurrentShot } from '../api/liveGames';
import { estimateQuadrantTime, sideFromBroomPosition, turnToDirection, endTypeFromEndNumber, QuadrantKey, DEFAULT_CURL_FEET } from '../utils/teeLineEstimate';

export interface Rock {
  id: number;
  team: 'team1' | 'team2';
  x: number;
  y: number;
}

interface BroomPositionData {
  zone: number;
  label: string;
  cwShots: number;
  ccwShots: number;
  totalShots: number;
  cwAvgCurl: number;
  ccwAvgCurl: number;
}

interface ShotTrackerProps {
  endNumber: number;
  team: 'team1' | 'team2';
  teamName: string;
  playerNames: string[];
  shotNumber: number;
  onShotRecorded: (shot: Omit<Shot, 'id' | 'timestamp'>, updatedRocks: Rock[]) => void;
  onClose: () => void;
  expectedTimes?: {
    teeLine: number;
    hack: number;
    board: number;
    control: number;
    normal: number;
    firm: number;
    peel: number;
  };
  team1Color?: string;
  team2Color?: string;
  initialRocks?: Rock[];
  rockOrder?: number[];
  playerAssignments?: number[];
  game?: Game; // Add game prop to access historical data
}

const shotResults: { value: ShotResult; label: string; color: string }[] = [
  { value: 'excellent', label: '4 pts', color: 'bg-green-500' },
  { value: 'good', label: '3 pts', color: 'bg-blue-500' },
  { value: 'fair', label: '2 pts', color: 'bg-yellow-500' },
  { value: 'poor', label: '1 pt', color: 'bg-orange-500' },
  { value: 'miss', label: '0 pts', color: 'bg-red-500' },
];

const weights: { value: Weight; label: string; description: string }[] = [
  { value: 'light', label: 'Light', description: 'Slow, gentle throw' },
  { value: 'good', label: 'Good', description: 'Normal speed' },
  { value: 'heavy', label: 'Heavy', description: 'Hard, fast throw' },
];

const weightReasons: { value: WeightReason; label: string; description: string }[] = [
  { value: 'sweeping', label: 'Team', description: 'Weight issue due to sweeping' },
  { value: 'ice/rock', label: 'Ice/Rock', description: 'Weight issue due to ice or rock' },
  { value: 'thrower', label: 'Thrower', description: 'Weight issue due to thrower' },
];

const curlAssessments: { value: CurlAssessment; label: string; description: string }[] = [
  { value: 'under', label: 'Under', description: 'Less curl than expected' },
  { value: 'good', label: 'Good', description: 'Curl on target' },
  { value: 'over', label: 'Over', description: 'More curl than expected' },
];

const curlReasons: { value: CurlReason; label: string; description: string }[] = [
  { value: 'n/a', label: 'Team', description: 'Line call issue' },
  { value: 'ice/rock', label: 'Ice/Rock', description: 'Curl issue due to ice or rock' },
  { value: 'thrower', label: 'Thrower', description: 'Curl issue due to thrower' },
];

// Shared with the server's ShotType enum. Zone 0 maps to 'hogged'; this list used to
// map it to 'high-guard' because no 'hogged' shot type existed.
const positionZones = POSITION_ZONES;

function ordinal(n: number): string {
  return `${n}${n === 1 ? 'st' : n === 2 ? 'nd' : n === 3 ? 'rd' : 'th'}`;
}

function zoneColors(zone: number): { bg: string; selected: string } {
  if (zone === 0) return { bg: 'bg-red-100', selected: 'bg-red-500 text-white' };
  if (zone <= 3)  return { bg: 'bg-green-100', selected: 'bg-green-600 text-white' };
  if (zone <= 10) return { bg: 'bg-blue-100', selected: 'bg-blue-500 text-white' };
  return { bg: 'bg-yellow-100', selected: 'bg-yellow-500 text-white' };
}

// Turn isn't tracked as its own state — it falls straight out of which side
// of the broom a pin sits on (positive curl-from-broom = clockwise, see
// turnToDirection in utils/teeLineEstimate). Expected-side estimates (before
// anything's been thrown) and the final submitted turn (what actually
// happened) can legitimately differ, so each is derived from its own value
// rather than one shared mutable value — that shared value was exactly what
// the earlier centerline-crossing auto-detect effect used to clobber.
const curlToTurn = (curlValue: number): Turn => (curlValue >= 0 ? 'clockwise' : 'counter-clockwise');

export function ShotTracker({ endNumber, team, teamName, playerNames, shotNumber, onShotRecorded, onClose, expectedTimes, team1Color, team2Color, initialRocks, rockOrder, playerAssignments, game }: ShotTrackerProps) {
  // Warm-up shots, for the same draw-weight estimate cascade the Sheet
  // Visualizer uses. Empty until loaded; the estimate below self-corrects
  // once it resolves (see the effect that recomputes expectedTime).
  const [practiceShots, setPracticeShots] = useState<PracticeShot[]>([]);
  useEffect(() => {
    if (!game?.id) return;
    let cancelled = false;
    getPracticeSession(game.id)
      .then(session => { if (!cancelled) setPracticeShots(session.shots ?? []); })
      .catch(error => console.error('Failed to load practice shots for the tee-line estimate:', error));
    return () => { cancelled = true; };
  }, [game?.id]);

  // Calculate number of rocks per team based on player count
  const rocksPerTeam = playerNames.length === 2 ? 6 : 8;

  // Get the actual rock number from the custom order (or default order)
  const defaultRockOrder = [1, 2, 3, 4, 5, 6, 7, 8];
  const currentRockOrder = rockOrder && rockOrder.length > 0 ? rockOrder : defaultRockOrder;
  const actualRockNumber = currentRockOrder[(shotNumber - 1) % currentRockOrder.length];

  // Calculate overall rock position in end (alternating between teams)
  const totalRocksInEnd = rocksPerTeam * 2;
  const overallRockNumber = team === 'team1'
    ? (shotNumber - 1) * 2 + 1
    : (shotNumber - 1) * 2 + 2;

  // Calculate player-specific shot count
  const getPlayerShotInfo = () => {
    const playerCount = playerNames.length;

    // Define shot patterns based on player count
    const shotPatterns: Record<number, number[]> = {
      2: [1, 2, 2, 2, 1], // 5 shots for 2 players
      3: [1, 1, 1, 2, 2, 2, 3, 3], // 8 shots for 3 players
      4: [1, 1, 2, 2, 3, 3, 4, 4], // 8 shots for 4 players
      5: [1, 1, 2, 2, 3, 3, 4, 4], // 8 shots for 4 players (5th is substitute)
    };

    let pattern = shotPatterns[playerCount] || [];

    // Use custom player assignments if available
    if (playerAssignments && playerAssignments.length > 0) {
      pattern = playerAssignments.filter(p => p !== -1).map(p => p + 1); // Convert to 1-based
    }

    if (pattern.length === 0) {
      return { playerShotNumber: shotNumber, totalPlayerShots: rocksPerTeam };
    }

    // Get which player is throwing this shot
    const patternIndex = (shotNumber - 1) % pattern.length;
    const currentPlayerIndex = pattern[patternIndex];

    // Count how many shots this player throws in total
    const totalPlayerShots = pattern.filter(p => p === currentPlayerIndex).length;

    // Count which number shot this is for this player
    let playerShotNumber = 0;
    for (let i = 0; i <= patternIndex; i++) {
      if (pattern[i] === currentPlayerIndex) {
        playerShotNumber++;
      }
    }

    return { playerShotNumber, totalPlayerShots };
  };

  const { playerShotNumber, totalPlayerShots } = getPlayerShotInfo();

  // Initialize rocks with default positions (single line above the hog line)
  const initializeRocks = (): Rock[] => {
    const rocks: Rock[] = [];
    let id = 0;
    
    // Team 1 rocks - positioned on left side in a single horizontal line above hog line
    for (let i = 0; i < rocksPerTeam; i++) {
      rocks.push({
        id: id++,
        team: 'team1',
        x: 148 + i * 15, // horizontal spacing of 15 pixels, overlapping left to right
        y: 12, // Single line above hog line (hog line is at y=27)
      });
    }
    
    // Team 2 rocks - positioned on right side in a single horizontal line above hog line, aligned to right border
    for (let i = 0; i < rocksPerTeam; i++) {
      rocks.push({
        id: id++,
        team: 'team2',
        x: 440 - i * 15, // overlapping right to left
        y: 12, // Single line above hog line (hog line is at y=27)
      });
    }
    
    return rocks;
  };
  
  const [rocks, setRocks] = useState<Rock[]>(initialRocks || initializeRocks());
  const [draggingRock, setDraggingRock] = useState<number | null>(null);
  const [nextRockMoved, setNextRockMoved] = useState(false); // Track if the next rock has been moved by user
  
  // Helper function to get pre-selected player based on shot number and player count
  const getPreSelectedPlayer = () => {
    // Use custom player assignments if available
    if (playerAssignments && playerAssignments.length > 0) {
      const position = (shotNumber - 1) % 8; // Get position 0-7
      const playerIndex = playerAssignments[position];
      
      if (playerIndex === -1) {
        return ''; // Unassigned
      }
      
      return playerNames[playerIndex] || playerNames[0] || '';
    }
    
    // Fall back to default pattern
    const playerCount = playerNames.length;
    
    // Define shot patterns based on player count
    const shotPatterns: Record<number, number[]> = {
      2: [1, 2, 2, 2, 1], // 5 shots for 2 players
      3: [1, 1, 1, 2, 2, 2, 3, 3], // 8 shots for 3 players
      4: [1, 1, 2, 2, 3, 3, 4, 4], // 8 shots for 4 players
      5: [1, 1, 2, 2, 3, 3, 4, 4], // 8 shots for 4 players (5th is substitute)
    };
    
    const pattern = shotPatterns[playerCount];
    if (!pattern) {
      // Default to first player if no pattern defined
      return playerNames[0] || '';
    }
    
    // Get player index from pattern (shotNumber is 1-based)
    const patternIndex = (shotNumber - 1) % pattern.length;
    const playerIndex = pattern[patternIndex] - 1; // Convert to 0-based index
    
    return playerNames[playerIndex] || playerNames[0] || '';
  };
  
  // Draw-to-tee-line baseline for the quadrant the shot is currently aimed at
  // (broom side + turn + which end) — the exact same cascade the Sheet
  // Visualizer uses: this quadrant's recent shots, else its mirror's first
  // shot, else a practice/game-setup baseline. See utils/teeLineEstimate.
  const getTeeLineBaselineEstimate = (): number => {
    if (!game) return expectedTimes?.teeLine || 14.5;
    const quadrant: QuadrantKey = {
      endType: endTypeFromEndNumber(endNumber),
      side: sideFromBroomPosition(broomPosition),
      direction: turnToDirection(expectedTurn),
    };
    return estimateQuadrantTime(game, practiceShots, quadrant, true).time;
  };

  // Initial expected time is the tee-line baseline for the default quadrant
  // (broomPosition/turn haven't been touched yet).
  const getInitialExpectedTime = (): number => getTeeLineBaselineEstimate();

  // Same fallback the Shot Visualizer's own curl estimate uses (see
  // estimateBinnedCurl in utils/teeLineEstimate): the game-setup default if
  // one was entered, else the shared last-resort constant — never a bare 0.
  const getInitialExpectedCurl = (): number => game?.expectedCurl ?? DEFAULT_CURL_FEET;

  const [playerName, setPlayerName] = useState(getPreSelectedPlayer());
  const [targetZone, setTargetZone] = useState(7); // Target zone for the shot (1-15), 0 means not selected
  const [broomPosition, setBroomPosition] = useState(0); // Default to center, feet from center
  // Matches the Shot Visualizer's own fallback, so a fresh shot with no
  // history yet shows the same expected curl in both places. Signed —
  // positive means the expected pin sits to the right of the broom. Declared
  // before expectedTime below: getInitialExpectedTime's baseline estimate
  // needs expectedTurn, which is derived from this.
  const [expectedCurl, setExpectedCurl] = useState(getInitialExpectedCurl());
  const expectedTurn = curlToTurn(expectedCurl);
  // The actual result mirrors the call (same zone, same curl-from-broom
  // offset) until the Shot Result Recorder is used to record where the rock
  // really finished — most shots land close enough to the call that this
  // saves a redundant step for anyone not tracking exact misses.
  const [actualRecorded, setActualRecorded] = useState(false);
  const [result, setResult] = useState<ShotResult>('good');
  const [expectedTime, setExpectedTime] = useState(getInitialExpectedTime());
  const [weight, setWeight] = useState<Weight>('good');
  const [weightReason, setWeightReason] = useState<WeightReason>('n/a');
  const [curlAssessment, setCurlAssessment] = useState<CurlAssessment>('good');
  const [curlReason, setCurlReason] = useState<CurlReason>('n/a');
  const [curl, setCurl] = useState(0);
  const [time, setTime] = useState(0);
  const [actualPosition, setActualPosition] = useState(7);
  const [isGoldStar, setIsGoldStar] = useState(false);
  const [isBomb, setIsBomb] = useState(false);
  const [hasVisualizerPlan, setHasVisualizerPlan] = useState(false);
  const [notes, setNotes] = useState('');
  const [specialShot, setSpecialShot] = useState<SpecialShot | null>(null);
  const [shotIssue, setShotIssue] = useState<ShotIssue>('none');
  const [broomPositionData, setBroomPositionData] = useState<BroomPositionData[]>([]);
  const [isStopwatchRunning, setIsStopwatchRunning] = useState(false);
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);
  const [viewMode, setViewMode] = useState<'interactive' | 'condensed'>('interactive');
  // Set by the Shot Visualizer's "Set" button so the two auto-estimate
  // effects below skip their own recompute for that state change — otherwise
  // they'd immediately overwrite the visualizer's own expectedCurl/expectedTime
  // with their own (different) estimate the moment targetZone/broomPosition change.
  const skipAutoEstimateRef = useRef(false);

  // Convert zone to shot type
  const getShotTypeFromZone = (zone: number): ShotType => {
    return positionZones[zone]?.shotType || 'tee-line';
  };

  // Calculate average time for any zone from historical game data
  const getZoneAverageTime = (targetZone: number): number | null => {
    if (!game) return null;

    const zoneTimes: number[] = [];

    // Collect all times from shots that ended in the target zone (ignore 0.0s times)
    game.ends.forEach(end => {
      end.shots.forEach(shot => {
        if (!shot.skipped && shot.actualPosition === targetZone && shot.time > 0) {
          zoneTimes.push(shot.time);
        }
      });
    });

    // Return average if we have data, otherwise null
    if (zoneTimes.length > 0) {
      return zoneTimes.reduce((sum, time) => sum + time, 0) / zoneTimes.length;
    }

    return null;
  };

  // Expected time for a target zone. Zones 1-11 (guards through hack) are the
  // tee-line quadrant baseline plus a fixed distance offset; zones 12-16 (hit
  // weights) use the setup values directly since they aren't tee-line draws.
  const getExpectedTimeFromZone = (zone: number): number => {
    const teeLineBaseline = getTeeLineBaselineEstimate();

    if (zone >= 1 && zone <= 11) {
      switch (zone) {
        case 1: return teeLineBaseline + 4.0;   // High Guard
        case 2: return teeLineBaseline + 3.0;   // Mid Guard
        case 3: return teeLineBaseline + 1.5;   // Tight Guard
        case 4: return teeLineBaseline + 0.75;  // Top Twelve
        case 5: return teeLineBaseline + 0.5;   // Top Eight
        case 6: return teeLineBaseline + 0.25;  // Top Four
        case 7: return teeLineBaseline;         // Tee Line
        case 8: return teeLineBaseline - 0.25;  // Back Four
        case 9: return teeLineBaseline - 0.5;   // Back Eight
        case 10: return teeLineBaseline - 0.75; // Back Twelve
        case 11: return teeLineBaseline - 2;    // Hack
      }
    }

    if (expectedTimes) {
      switch (zone) {
        case 12: return expectedTimes.board;
        case 13: return expectedTimes.control;
        case 14: return expectedTimes.normal;
        case 15: return expectedTimes.firm;
        case 16: return expectedTimes.peel;
      }
    }

    return teeLineBaseline;
  };

  // Applies a shot planned in the Shot Visualizer: its own expected curl and
  // weight estimates replace this form's, rather than letting the two
  // auto-estimate effects above immediately recompute (and likely diverge
  // from) whatever the visualizer just showed. Turn isn't set directly — it
  // falls out of expectedCurl's sign once that's applied.
  const handleApplyFromVisualizer = (values: ShotVisualizerAppliedValues) => {
    skipAutoEstimateRef.current = true;
    setHasVisualizerPlan(true);
    setTargetZone(values.targetZone);
    setBroomPosition(values.broomPosition);
    setExpectedCurl(values.expectedCurl);
    setExpectedTime(values.expectedTime);
    // A fresh plan resets any earlier recorded result, same as a new call
    // superseding whatever was previously recorded against the old one.
    setActualPosition(values.targetZone);
    setCurl(values.expectedCurl);
    setActualRecorded(false);
  };

  // Applies the Shot Result Recorder's "Record" button — the actual pin no
  // longer mirrors the call once a real result has been recorded.
  const handleRecordActualResult = (values: ShotResultRecorderAppliedValues) => {
    setActualPosition(values.actualPosition);
    setCurl(values.curl);
    setActualRecorded(true);
  };

  // The "Analyze Shot" step: auto-assesses weight/curl against the call and
  // opens the analysis modal. Takes optional overrides so the Shot Result
  // Recorder's own "Analyze Shot" button can run this immediately against a
  // just-recorded result without waiting on the setActualPosition/setCurl
  // state updates above to land first — actualPosition/curl (read via the
  // defaults below) only reflect a result recorded on a *previous* render.
  const handleAnalyzeShot = (overrideActualPosition?: number, overrideCurl?: number) => {
    const effectiveActualPosition = overrideActualPosition ?? actualPosition;
    const effectiveCurl = overrideCurl ?? curl;

    // If the next rock hasn't been moved, update its position to match the actual pin
    if (!nextRockMoved) {
      const nextRockId = team === 'team1'
        ? (actualRockNumber - 1)
        : (actualRockNumber - 1 + rocksPerTeam);

      setRocks(prevRocks => {
        const updatedRocks = prevRocks.map(rock => {
          if (rock.id === nextRockId) {
            return {
              ...rock,
              // Same feet-to-pixel scale initializeRocks uses elsewhere in
              // this file; zone maps to y linearly since nothing renders
              // this mini-map's exact geometry (it only feeds mixed-doubles
              // rock-order persistence, see onShotRecorded's second argument).
              x: 296 + (broomPosition + effectiveCurl) * 24,
              y: 12 + effectiveActualPosition * 30,
            };
          }
          return rock;
        });
        return updatedRocks;
      });

      // Mark the rock as moved so it stays at this position
      setNextRockMoved(true);
    }

    // Auto-analyze shot based on actual vs expected values
    // Weight assessment: compare actual time vs expected time
    // If time is 0.0s, treat as good/medium weight with no assessment
    if (time === 0 || time <= 0) {
      setWeight('good');
      setWeightReason('n/a');
    } else {
      const timeDiff = time - expectedTime;
      if (timeDiff < -0.25) {
        setWeight('heavy');
        setWeightReason('early');
      } else if (timeDiff > 0.5) {
        setWeight('light');
        setWeightReason('late');
      } else {
        setWeight('good');
        setWeightReason('on-time');
      }
    }

    // Curl assessment — effectiveCurl/expectedCurl are the live, authoritative
    // values the diagrams last produced, no resync needed.
    const curlDifferenceInches = (effectiveCurl - expectedCurl) * 12;

    if (curlDifferenceInches < -4) {
      setCurlAssessment('under');
      setCurlReason('n/a');
    } else if (curlDifferenceInches > 4) {
      setCurlAssessment('over');
      setCurlReason('n/a');
    } else {
      setCurlAssessment('good');
      setCurlReason('n/a');
    }

    // Broadcast the plan — target zone and the expected values it was
    // judged against — now that Analyze Shot has been pressed. Weight/curl
    // assessment and points follow once Record Shot is actually pressed.
    if (game?.id) {
      pushCurrentShot(game.id, {
        endNumber, team, playerName: playerName.trim() || playerName, shotNumber,
        shotType: getShotTypeFromZone(targetZone),
        expectedTime,
        expectedCurl,
      }).catch(() => {});
    }

    // Open the analysis modal
    setShowAnalysisModal(true);
  };

  // The Shot Result Recorder's "Analyze Shot" button — records the result
  // and immediately runs the same analysis the main page's own Analyze Shot
  // button does, passing the just-recorded values straight through rather
  // than relying on actualPosition/curl state that hasn't re-rendered yet.
  const handleRecordAndAnalyze = (values: ShotResultRecorderAppliedValues) => {
    handleRecordActualResult(values);
    handleAnalyzeShot(values.actualPosition, values.curl);
  };

  // Rock drag handlers
  const handleRockMouseDown = (rockId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setDraggingRock(rockId);
    
    // Determine which rock is "next to be thrown"
    const nextRockId = team === 'team1' 
      ? (actualRockNumber - 1) // team1 rocks are 0-3
      : (actualRockNumber - 1 + rocksPerTeam); // team2 rocks are 4-7
    
    // If this is the next rock being dragged, mark it as moved
    if (rockId === nextRockId) {
      setNextRockMoved(true);
    }
  };

  const handleRockMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
    if (draggingRock === null) return;
    
    const svg = e.currentTarget;
    const pt = svg.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    const svgP = pt.matrixTransform(svg.getScreenCTM()?.inverse());
    
    // Constrain to ice surface (x: 140-452, y: 0-577, allowing above hog line)
    const constrainedX = Math.max(140, Math.min(452, svgP.x));
    const constrainedY = Math.max(0, Math.min(577, svgP.y));
    
    setRocks(prev => prev.map(rock => 
      rock.id === draggingRock 
        ? { ...rock, x: constrainedX, y: constrainedY }
        : rock
    ));
  };

  const handleRockMouseUp = () => {
    setDraggingRock(null);
  };

  // Result follows only the Weight/Curl/Shot Issue buttons, no other input:
  // any shot issue forces a miss (0 pts); otherwise the baseline is excellent
  // (4 pts), reduced 1 pt for each of weight/curl that isn't Good — so one
  // off-target is good (3 pts), both is poor (1 pt).
  useEffect(() => {
    if (shotIssue !== 'none') {
      setResult('miss');
      return;
    }
    const badCount = (weight !== 'good' ? 1 : 0) + (curlAssessment !== 'good' ? 1 : 0);
    setResult(badCount === 0 ? 'excellent' : badCount === 1 ? 'good' : 'poor');
  }, [weight, curlAssessment, shotIssue]);

  // Load broom position data from game when component mounts
  useEffect(() => {
    if (!game) return;
    
    // Calculate broom position data from historical game data
    const horizontalRegions = [
      { minPos: -6, maxPos: -5, label: '6 ft Left' },
      { minPos: -5, maxPos: -4, label: '5 ft Left' },
      { minPos: -4, maxPos: -3, label: '4 ft Left' },
      { minPos: -3, maxPos: -2, label: '3 ft Left' },
      { minPos: -2, maxPos: -1, label: '2 ft Left' },
      { minPos: -1, maxPos: 0, label: '1 ft Left' },
      { minPos: 0, maxPos: 1, label: '1 ft Right' },
      { minPos: 1, maxPos: 2, label: '2 ft Right' },
      { minPos: 2, maxPos: 3, label: '3 ft Right' },
      { minPos: 3, maxPos: 4, label: '4 ft Right' },
      { minPos: 4, maxPos: 5, label: '5 ft Right' },
      { minPos: 5, maxPos: 6, label: '6 ft Right' },
    ];

    const horizontalCounts = horizontalRegions.map(region => ({
      label: region.label,
      minPos: region.minPos,
      maxPos: region.maxPos,
      cw: 0,
      ccw: 0,
      cwCurlSum: 0,
      ccwCurlSum: 0,
      cwCurlCount: 0,
      ccwCurlCount: 0,
    }));

    game.ends.forEach(end => {
      end.shots.forEach(shot => {
        if (!shot.skipped) {
          const intendedZone = getZoneFromShotType(shot.shotType);
          
          if (intendedZone >= 4 && intendedZone <= 10) {
            const broomPos = shot.broomPosition;
            const region = horizontalCounts.find(r => broomPos >= r.minPos && broomPos < r.maxPos);
            
            if (region) {
              if (shot.turn === 'clockwise') {
                region.cw++;
                if (intendedZone >= 5 && intendedZone <= 7) {
                  region.cwCurlSum += shot.curl;
                  region.cwCurlCount++;
                }
              } else {
                region.ccw++;
                if (intendedZone >= 5 && intendedZone <= 7) {
                  region.ccwCurlSum += shot.curl;
                  region.ccwCurlCount++;
                }
              }
            }
          }
        }
      });
    });

    const data: BroomPositionData[] = horizontalCounts.map((region, index) => ({
      zone: index,
      label: region.label,
      cwShots: region.cw,
      ccwShots: region.ccw,
      totalShots: region.cw + region.ccw,
      cwAvgCurl: region.cwCurlCount > 0 ? region.cwCurlSum / region.cwCurlCount : 0,
      ccwAvgCurl: region.ccwCurlCount > 0 ? region.ccwCurlSum / region.ccwCurlCount : 0,
    }));

    setBroomPositionData(data);
  }, [game]);

  // Update expected curl when the target zone, broom position, or turn
  // changes — mirrors the expected-time effect below, which already
  // includes targetZone for the same reason: a plain zone-button click
  // (unlike a Visualizer "Set") never touches broomPosition/turn itself, so
  // without targetZone here this estimate would never refresh on a zone
  // click and would keep showing whatever was last computed (possibly the
  // Visualizer's own value, now stale for the newly selected zone).
  useEffect(() => {
    if (skipAutoEstimateRef.current) return;
    if (broomPositionData.length === 0) return;

    // Map broom position (-7 to 7) to horizontal region
    const horizontalRegions = [
      { minPos: -7, maxPos: -5, index: 0 }, // 6 ft Left
      { minPos: -5, maxPos: -4, index: 1 }, // 5 ft Left
      { minPos: -4, maxPos: -3, index: 2 }, // 4 ft Left
      { minPos: -3, maxPos: -2, index: 3 }, // 3 ft Left
      { minPos: -2, maxPos: -1, index: 4 }, // 2 ft Left
      { minPos: -1, maxPos: 0, index: 5 }, // 1 ft Left
      { minPos: 0, maxPos: 1, index: 6 }, // 1 ft Right
      { minPos: 1, maxPos: 2, index: 7 }, // 2 ft Right
      { minPos: 2, maxPos: 3, index: 8 }, // 3 ft Right
      { minPos: 3, maxPos: 4, index: 9 }, // 4 ft Right
      { minPos: 4, maxPos: 5, index: 10 }, // 5 ft Right
      { minPos: 5, maxPos: 7, index: 11 }, // 6 ft Right
    ];

    const region = horizontalRegions.find(r => broomPosition >= r.minPos && broomPosition < r.maxPos);

    if (region) {
      const data = broomPositionData[region.index];
      if (data) {
        const avgCurl = expectedTurn === 'clockwise' ? data.cwAvgCurl : data.ccwAvgCurl;
        if (avgCurl > 0) {
          // Round to nearest 0.25 feet
          const rounded = Math.round(avgCurl * 4) / 4;
          setExpectedCurl(rounded);
          if (!actualRecorded) setCurl(rounded); // Mirror expected until a real result is recorded
        }
      }
    }
  }, [targetZone, broomPosition, expectedTurn, broomPositionData, actualRecorded]);

  // Update expected time whenever the target zone, broom side, or turn
  // changes — all three select the quadrant getExpectedTimeFromZone estimates
  // from.
  useEffect(() => {
    if (skipAutoEstimateRef.current) return;
    setExpectedTime(getExpectedTimeFromZone(targetZone));
  }, [targetZone, broomPosition, expectedTurn, practiceShots]);

  // Runs after both auto-estimate effects above (declared earlier, same
  // commit), clearing the skip so the next ordinary zone/broom-position/curl
  // change — from tapping the diagram, not from the visualizer's Set button —
  // auto-estimates as usual.
  useEffect(() => {
    skipAutoEstimateRef.current = false;
  }, [targetZone, broomPosition, expectedTurn]);

  // Stopwatch timer logic
  const startTimeRef = useRef<number>(0);
  const intervalRef = useRef<number | null>(null);

  useEffect(() => {
    if (isStopwatchRunning) {
      // Set the start time when timer begins
      startTimeRef.current = Date.now() - time * 1000;
      
      intervalRef.current = window.setInterval(() => {
        const newTime = (Date.now() - startTimeRef.current) / 1000;
        setTime(newTime);
      }, 10);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isStopwatchRunning, time]);

  // Reset form when shot number or team changes (for continuous recording)
  useEffect(() => {
    const nextPlayer = getPreSelectedPlayer();

    // Reset all form fields to defaults
    setPlayerName(nextPlayer);
    setTargetZone(7);
    setBroomPosition(-3.93);
    // Weight and curl both reset to 'good' below, so the baseline result is
    // 'excellent' (4pts) — the shot-scoring effect won't refire to correct
    // this on its own if those values were already 'good' from the prior shot.
    setResult('excellent');
    setExpectedTime(getInitialExpectedTime());
    setExpectedCurl(getInitialExpectedCurl());
    setActualRecorded(false);
    setWeight('good');
    setWeightReason('n/a');
    setCurlAssessment('good');
    setCurlReason('n/a');
    // Actual mirrors expected until a real result is recorded — same as
    // targetZone/expectedCurl just above, not a bare 0.
    setCurl(getInitialExpectedCurl());
    setTime(0);
    setActualPosition(7);
    setIsGoldStar(false);
    setIsBomb(false);
    setHasVisualizerPlan(false);
    setNotes('');
    setSpecialShot(null);
    setShotIssue('none');
    setShowAnalysisModal(false);
    setNextRockMoved(false);

    // Broadcast who's up as soon as the shot starts — "plan in progress, result not
    // entered" until Analyze Shot/Record Shot fill the rest in below.
    if (game?.id) {
      pushCurrentShot(game.id, { endNumber, team, playerName: nextPlayer, shotNumber }).catch(() => {});
    }
  }, [shotNumber, team]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!playerName.trim()) return;

    // If time is 0.0s, treat it as invalid - set weight to "good" and weightReason to "n/a"
    const isValidTime = time > 0;
    const finalWeight = isValidTime ? weight : 'good';
    const finalWeightReason = isValidTime ? weightReason : 'n/a';

    // Broadcast the result now that Record Shot has been pressed — same shot slot as
    // the plan pushed at Analyze Shot, so the live view updates it in place.
    if (game?.id) {
      pushCurrentShot(game.id, {
        endNumber, team, playerName: playerName.trim(), shotNumber,
        shotType: getShotTypeFromZone(targetZone),
        expectedTime,
        expectedCurl,
        actualZone: getShotTypeFromZone(actualPosition),
        weight: finalWeight,
        curlAssessment,
        result,
      }).catch(() => {});
    }

    onShotRecorded({
      endNumber,
      playerName: playerName.trim(),
      team,
      shotType: getShotTypeFromZone(targetZone),
      result,
      expectedTime,
      expectedCurl,
      weight: finalWeight,
      weightReason: finalWeightReason,
      curlAssessment,
      curlReason,
      curl,
      time,
      broomPosition,
      // Turn describes what was actually thrown, so it's derived from the
      // actual pin's side of the broom, not the expected/called one.
      turn: curlToTurn(curl),
      actualPosition,
      isGoldStar,
      isBomb,
      hasVisualizerPlan: hasVisualizerPlan || undefined,
      notes: notes.trim() || undefined,
      specialShot: specialShot || undefined,
      issue: shotIssue !== 'none' ? shotIssue : undefined,
    }, rocks);

    // Don't call onClose() - let the parent (LiveGame) decide whether to close or continue to next shot
  };

  const handleSkipShot = () => {
    if (game?.id) {
      pushCurrentShot(game.id, {
        endNumber, team, playerName: 'Unknown', shotNumber,
        shotType: 'tee-line', expectedTime: 12, expectedCurl: 3,
        actualZone: 'back-four', weight: 'good', curlAssessment: 'good', result: 'fair',
      }).catch(() => {});
    }

    // Record a shot with default/placeholder values
    onShotRecorded({
      endNumber,
      playerName: 'Unknown',
      team,
      shotType: 'tee-line',
      result: 'fair',
      expectedTime: 12,
      expectedCurl: 3,
      weight: 'good',
      curl: 0,
      time: 0,
      broomPosition: 0,
      turn: 'clockwise',
      actualPosition: 8,
      skipped: true,
    }, rocks);

    // Don't call onClose() - let the parent component decide whether to continue or close
  };

  return (
    <div className="fixed inset-0 bg-white z-50 overflow-y-auto">
      <div className="min-h-screen max-w-6xl mx-auto p-4 md:p-6">
        <div className="flex items-center justify-between mb-6 sticky top-0 bg-white py-4 border-b z-10">
          <div className="flex items-center gap-2">
            <Target className="w-6 h-6 text-blue-600" />
            <h2 className="text-xl font-semibold">Record Shot - {teamName}</h2>
          </div>
          <button
            onClick={onClose}
            className="flex items-center gap-2 text-blue-600 hover:text-blue-800 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Summary
          </button>
        </div>

        <div className="mb-4">
          <div className="flex gap-2">
            {playerNames.map((name) => (
              <button
                key={name}
                type="button"
                onClick={() => setPlayerName(name)}
                className={`flex-1 px-3 py-2 rounded-md border transition-colors ${
                  playerName === name
                    ? 'text-white'
                    : 'bg-white hover:bg-gray-50'
                }`}
                style={playerName === name ? {
                  backgroundColor: team === 'team1' ? team1Color : team2Color,
                  borderColor: team === 'team1' ? team1Color : team2Color,
                } : {
                  borderColor: '#d1d5db',
                }}
              >
                <div>{name}</div>
                {playerName === name && (
                  <div className="text-sm text-black font-bold">
                    {ordinal(playerShotNumber)} of {totalPlayerShots} · Rock #{actualRockNumber}
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>

        <div className="flex gap-2 mb-4">
          {game && (
            <ShotVisualizer
              key={`${endNumber}-${team}-${shotNumber}`}
              game={game}
              endNumber={endNumber}
              className="flex-1"
              onApply={handleApplyFromVisualizer}
            />
          )}
          <button
            type="button"
            onClick={handleSkipShot}
            className="flex-1 bg-gray-200 text-gray-700 py-2 rounded-md hover:bg-gray-300 transition-colors flex items-center justify-center gap-2"
          >
            <SkipForward className="w-4 h-4" />
            Skip Shot (No Data)
          </button>
        </div>
        {/* <div className="flex gap-2 mb-4">
          <button
            type="button"
            onClick={handleSkipShot}
            className="flex-1 bg-gray-200 text-gray-700 py-2 rounded-md hover:bg-gray-300 transition-colors flex items-center justify-center gap-2"
          >
            <SkipForward className="w-4 h-4" />
            Skip Shot
          </button>
          <button
            type="button"
            onClick={() => setViewMode(viewMode === 'interactive' ? 'condensed' : 'interactive')}
            className="px-4 bg-blue-100 text-blue-700 py-2 rounded-md hover:bg-blue-200 transition-colors flex items-center justify-center gap-2"
          >
            {viewMode === 'interactive' ? <Grid3x3 className="w-4 h-4" /> : <Hand className="w-4 h-4" />}
            {viewMode === 'interactive' ? 'Numeric' : 'Interactive'}
          </button>
        </div> */}

        <form onSubmit={handleSubmit} className="space-y-4 pb-8">
          {/* Condensed Numeric View */}
          <div className="bg-gradient-to-b from-gray-50 to-gray-100 rounded-lg p-4 border border-gray-200">
            <div className="grid grid-cols-2 gap-4">
                {/* ── The call — read-only review of whatever the Shot Visualizer set above; not editable here ── */}
                <div className="col-span-2 bg-white rounded-lg p-3 border border-gray-200 space-y-2">
                  <label className="block text-sm font-medium">The Call</label>
                  <div className="flex items-center justify-center gap-8">
                    <div className="text-center">
                      <div className="text-xs text-gray-500">Zone</div>
                      <div className="text-xl font-bold text-gray-800">{positionZones[targetZone]?.label ?? targetZone}</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xs text-gray-500">Curl</div>
                      <div className="font-mono text-2xl font-bold text-sky-600">{expectedCurl >= 0 ? '+' : ''}{expectedCurl.toFixed(1)} ft</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xs text-gray-500">Turn</div>
                      <div className="text-xl font-bold" style={{ color: expectedTurn === 'clockwise' ? '#dc2626' : '#2563eb' }}>
                        {expectedTurn === 'clockwise' ? 'CW' : 'CCW'}
                      </div>
                    </div>
                  </div>
                  {!hasVisualizerPlan && (
                    <p className="text-xs text-gray-400 text-center">Default call — use Shot Visualizer above to set it.</p>
                  )}
                </div>

                {/* ── GROUP 3: Time ── */}
                <div className="col-span-2 bg-white rounded-lg p-3 border border-gray-200 space-y-2">
                  <label className="text-sm font-medium">Expected and Actual Time</label>
                  <div className="flex items-center justify-center gap-8">
                    <div className="text-center">
                      <div className="text-xs text-gray-500">Expected</div>
                      <div className="font-mono text-3xl font-bold text-blue-600">{expectedTime.toFixed(1)}s</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xs text-gray-500">Actual</div>
                      <div className="font-mono text-3xl font-bold text-blue-600">{time.toFixed(2)}s</div>
                    </div>
                  </div>
                  <input type="range" min="5" max="22" step="0.1" value={expectedTime}
                    onChange={(e) => setExpectedTime(Number(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider-large-thumb" />
                  <div className="flex justify-between text-xs text-gray-500"><span>5s</span><span>22s</span></div>

                  <div className="flex items-center justify-center gap-3 pt-1">
                    <button
                      type="button"
                      onClick={() => setIsStopwatchRunning(!isStopwatchRunning)}
                      className={`flex items-center gap-2 px-4 py-2 rounded-md font-medium transition-colors ${
                        isStopwatchRunning ? 'bg-red-500 hover:bg-red-400 text-white' : 'bg-sky-500 hover:bg-sky-400 text-white'
                      }`}
                    >
                      {isStopwatchRunning ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                      {isStopwatchRunning ? 'Stop' : 'Start'}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setIsStopwatchRunning(false); setTime(0); }}
                      className="flex items-center gap-2 px-4 py-2 rounded-md font-medium bg-gray-200 hover:bg-gray-300 text-gray-700 transition-colors"
                      title="Reset"
                    >
                      <RotateCcw className="w-4 h-4" />
                      Reset
                    </button>
                  </div>
                </div>

                {/* ── The result — only reachable once the stopwatch has actually run once ── */}
                {time > 0 && (
                  <div className="col-span-2 bg-white rounded-lg p-3 border border-gray-200 space-y-2">
                    <label className="block text-sm font-medium">The Result</label>
                    {actualRecorded && (
                      <div className="flex items-center justify-center gap-8">
                        <div className="text-center">
                          <div className="text-xs text-gray-500">Zone</div>
                          <div className="text-xl font-bold text-gray-800">{positionZones[actualPosition]?.label ?? actualPosition}</div>
                        </div>
                        <div className="text-center">
                          <div className="text-xs text-gray-500">Curl</div>
                          <div className="font-mono text-2xl font-bold text-lime-600">{curl >= 0 ? '+' : ''}{curl.toFixed(1)} ft</div>
                        </div>
                        <div className="text-center">
                          <div className="text-xs text-gray-500">Turn</div>
                          <div className="text-xl font-bold" style={{ color: curlToTurn(curl) === 'clockwise' ? '#dc2626' : '#2563eb' }}>
                            {curlToTurn(curl) === 'clockwise' ? 'CW' : 'CCW'}
                          </div>
                        </div>
                      </div>
                    )}
                    <ShotResultRecorder
                      key={`${endNumber}-${team}-${shotNumber}`}
                      endNumber={endNumber}
                      broomPosition={broomPosition}
                      expectedCurl={expectedCurl}
                      targetZone={targetZone}
                      className="w-full"
                      onRecord={handleRecordActualResult}
                      onRecordAndAnalyze={handleRecordAndAnalyze}
                    />
                    {!actualRecorded && (
                      <p className="text-xs text-gray-400 text-center">Not recorded yet — defaults to matching the call.</p>
                    )}
                  </div>
                )}

                {/* Slider thumb styling */}
                <style>{`
                  .slider-large-thumb::-webkit-slider-thumb {
                    -webkit-appearance: none;
                    appearance: none;
                    width: 24px;
                    height: 24px;
                    border-radius: 50%;
                    background: #3b82f6;
                    cursor: pointer;
                    border: 3px solid white;
                    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
                  }
                  .slider-large-thumb::-moz-range-thumb {
                    width: 24px;
                    height: 24px;
                    border-radius: 50%;
                    background: #3b82f6;
                    cursor: pointer;
                    border: 3px solid white;
                    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
                  }
                `}</style>

                {/* Actual Curl - Hidden */}
                {/* <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">Actual Curl (inches)</label>
                  <input
                    type="number"
                    step="0.5"
                    min="-72"
                    max="72"
                    value={curl.toFixed(1)}
                    onChange={(e) => setCurl(Number(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div> */}
              </div>
            </div>

          {/* Special Shot Section */}
          {targetZone > 3 && (
            <div className="grid grid-cols-3 gap-2">
              {targetZone >= 4 && targetZone <= 10 ? (
                // Draw shots (zones 4-10): Split, Tap, Freeze
                <>
                  <button
                    type="button"
                    onClick={() => setSpecialShot(specialShot === 'split' ? null : 'split')}
                    className={`px-3 py-2 rounded-md border transition-colors ${
                      specialShot === 'split'
                        ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-500'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    Split
                  </button>
                  <button
                    type="button"
                    onClick={() => setSpecialShot(specialShot === 'tap' ? null : 'tap')}
                    className={`px-3 py-2 rounded-md border transition-colors ${
                      specialShot === 'tap'
                        ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-500'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    Tap
                  </button>
                  <button
                    type="button"
                    onClick={() => setSpecialShot(specialShot === 'freeze' ? null : 'freeze')}
                    className={`px-3 py-2 rounded-md border transition-colors ${
                      specialShot === 'freeze'
                        ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-500'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    Freeze
                  </button>
                </>
              ) : (
                // Guard/Hit shots (zones 0-3, 11-15): Raise, Double, Roll Out, Roll, Multi Rock, Peel
                <>
                  <button
                    type="button"
                    onClick={() => setSpecialShot(specialShot === 'raise' ? null : 'raise')}
                    className={`px-3 py-2 rounded-md border transition-colors ${
                      specialShot === 'raise'
                        ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-500'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    Raise
                  </button>
                  <button
                    type="button"
                    onClick={() => setSpecialShot(specialShot === 'double' ? null : 'double')}
                    className={`px-3 py-2 rounded-md border transition-colors ${
                      specialShot === 'double'
                        ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-500'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    Double
                  </button>
                  <button
                    type="button"
                    onClick={() => setSpecialShot(specialShot === 'roll-out' ? null : 'roll-out')}
                    className={`px-3 py-2 rounded-md border transition-colors ${
                      specialShot === 'roll-out'
                        ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-500'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    Roll Out
                  </button>
                  <button
                    type="button"
                    onClick={() => setSpecialShot(specialShot === 'roll' ? null : 'roll')}
                    className={`px-3 py-2 rounded-md border transition-colors ${
                      specialShot === 'roll'
                        ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-500'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    Roll
                  </button>
                  <button
                    type="button"
                    onClick={() => setSpecialShot(specialShot === 'multi-rock' ? null : 'multi-rock')}
                    className={`px-3 py-2 rounded-md border transition-colors ${
                      specialShot === 'multi-rock'
                        ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-500'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    Multi Rock
                  </button>
                  <button
                    type="button"
                    onClick={() => setSpecialShot(specialShot === 'peel' ? null : 'peel')}
                    className={`px-3 py-2 rounded-md border transition-colors ${
                      specialShot === 'peel'
                        ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-500'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    Peel
                  </button>
                </>
              )}
            </div>
          )}

          <button
            type="button"
            onClick={() => handleAnalyzeShot()}
            className="w-full bg-purple-600 text-white py-2 px-4 rounded-md hover:bg-purple-700 transition-colors flex items-center justify-center gap-2 mb-4"
          >
            <Target className="w-4 h-4" />
            Analyze Shot
          </button>
        </form>
      </div>

      {/* Analysis Modal */}
      {showAnalysisModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold">Shot Analysis & Recording</h3>
              <button
                onClick={() => setShowAnalysisModal(false)}
                className="p-1 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <form onSubmit={(e) => {
                handleSubmit(e);
                setShowAnalysisModal(false);
              }} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Weight Assessment <span className="text-blue-600 font-medium">Expected: {expectedTime.toFixed(2)}s</span> <span className="text-green-600 font-medium">Actual: {time.toFixed(2)}s</span>
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {weights.map(({ value, label, description }) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => {
                          setWeight(value);
                          if (value === 'good') setWeightReason('n/a');
                        }}
                        className={`px-3 py-2 rounded-md border transition-colors text-center ${
                          weight === value
                            ? value === 'light' 
                              ? 'bg-yellow-500 text-white border-yellow-600'
                              : value === 'heavy'
                              ? 'bg-red-500 text-white border-red-600'
                              : 'bg-green-600 text-white border-green-700'
                            : 'bg-white border-gray-300 hover:border-purple-400'
                        }`}
                        title={description}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                {(weight === 'light' || weight === 'heavy') && (
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Reason for Weight Error
                    </label>
                    <div className="grid grid-cols-3 gap-2">
                      {weightReasons.map(({ value, label, description }) => (
                        <button
                          key={value}
                          type="button"
                          onClick={() => setWeightReason(value)}
                          className={`px-3 py-2 rounded-md border transition-colors text-center ${
                            weightReason === value
                              ? 'bg-purple-600 text-white border-purple-700'
                              : 'bg-white border-gray-300 hover:border-purple-400'
                          }`}
                          title={description}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium mb-2">
                    Curl Assessment <span className="text-blue-600 font-medium">Expected: {Math.abs(expectedCurl).toFixed(1)}ft</span> <span className="text-green-600 font-medium">Actual: {Math.abs(curl).toFixed(1)}ft</span>
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {curlAssessments.map(({ value, label, description }) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => {
                          setCurlAssessment(value);
                          if (value === 'good') setCurlReason('n/a');
                        }}
                        className={`px-3 py-2 rounded-md border transition-colors text-center ${
                          curlAssessment === value
                            ? value === 'under' 
                              ? 'bg-yellow-500 text-white border-yellow-600'
                              : value === 'over'
                              ? 'bg-red-500 text-white border-red-600'
                              : 'bg-green-600 text-white border-green-700'
                            : 'bg-white border-gray-300 hover:border-purple-400'
                        }`}
                        title={description}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                {(curlAssessment === 'under' || curlAssessment === 'over') && (
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Reason for Curl Error
                    </label>
                    <div className="grid grid-cols-3 gap-2">
                      {curlReasons.map(({ value, label, description }) => (
                        <button
                          key={value}
                          type="button"
                          onClick={() => setCurlReason(value)}
                          className={`px-3 py-2 rounded-md border transition-colors text-center ${
                            curlReason === value
                              ? 'bg-purple-600 text-white border-purple-700'
                              : 'bg-white border-gray-300 hover:border-purple-400'
                          }`}
                          title={description}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium mb-2">
                    Shot Issue
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    <button
                      type="button"
                      onClick={() => setShotIssue(shotIssue === 'crash' ? 'none' : 'crash')}
                      className={`px-3 py-2 rounded-md border transition-colors text-center ${
                        shotIssue === 'crash'
                          ? 'bg-orange-600 text-white border-orange-700'
                          : 'bg-white border-gray-300 hover:border-orange-400'
                      }`}
                    >
                      Crash
                    </button>
                    {actualPosition >= 4 && actualPosition <= 10 && (
                      <button
                        type="button"
                        onClick={() => setShotIssue(shotIssue === 'bounced' ? 'none' : 'bounced')}
                        className={`px-3 py-2 rounded-md border transition-colors text-center ${
                          shotIssue === 'bounced'
                            ? 'bg-orange-600 text-white border-orange-700'
                            : 'bg-white border-gray-300 hover:border-orange-400'
                        }`}
                      >
                        Bounced
                      </button>
                    )}
                    {actualPosition >= 11 && actualPosition <= 16 && (
                      <button
                        type="button"
                        onClick={() => setShotIssue(shotIssue === 'flash' ? 'none' : 'flash')}
                        className={`px-3 py-2 rounded-md border transition-colors text-center ${
                          shotIssue === 'flash'
                            ? 'bg-orange-600 text-white border-orange-700'
                            : 'bg-white border-gray-300 hover:border-orange-400'
                        }`}
                      >
                        Flash
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => setShotIssue(shotIssue === 'rule violation' ? 'none' : 'rule violation')}
                      className={`px-3 py-2 rounded-md border transition-colors text-center ${
                        shotIssue === 'rule violation'
                          ? 'bg-orange-600 text-white border-orange-700'
                          : 'bg-white border-gray-300 hover:border-orange-400'
                      }`}
                    >
                      Rule Violation
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    Result
                  </label>
                  <div className="grid grid-cols-5 gap-2">
                    {shotResults.map(({ value, label, color }) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => setResult(value)}
                        className={`px-3 py-2 rounded-md border transition-colors flex items-center justify-center gap-2 ${
                          result === value
                            ? 'border-gray-900 ring-2 ring-gray-900'
                            : 'border-gray-300 hover:border-gray-400'
                        }`}
                      >
                        <div className={`w-3 h-3 rounded-full ${color}`} />
                        {label}
                      </button>
                    ))}
                  </div>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <button
                      type="button"
                      onClick={() => {
                        setIsGoldStar(!isGoldStar);
                        if (!isGoldStar) setIsBomb(false);
                      }}
                      className={`px-3 py-2 rounded-md border transition-colors flex items-center justify-center gap-2 ${
                        isGoldStar
                          ? 'border-yellow-500 bg-yellow-50 ring-2 ring-yellow-500'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      <Star className={`w-5 h-5 ${isGoldStar ? 'fill-yellow-500 text-yellow-500' : 'text-gray-500'}`} />
                      Gold Star
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setIsBomb(!isBomb);
                        if (!isBomb) setIsGoldStar(false);
                      }}
                      className={`px-3 py-2 rounded-md border transition-colors flex items-center justify-center gap-2 ${
                        isBomb
                          ? 'border-red-500 bg-red-50 ring-2 ring-red-500'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      <Bomb className={`w-5 h-5 ${isBomb ? 'text-red-500' : 'text-gray-500'}`} />
                      Bomb
                    </button>
                  </div>
                </div>

                {/* Notes Section */}
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Notes
                  </label>
                  <input
                    type="text"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Optional notes about this shot"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <button
                  type="submit"
                  className="w-full bg-blue-600 text-white py-2 rounded-md hover:bg-blue-700 transition-colors"
                >
                  Record Shot
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}